import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import { BillingAddress } from "../../../types/payment"
import bbApi from "../../../api"
import { LightBtn, PrimaryBtn } from "../buttons"

interface BillingAddressFormProps {
  initialValues: BillingAddress
  orgId: string
  onSuccess: () => void
  onCancel: () => void
}

const addressSchema = Yup.object().shape({
  line1: Yup.string().required("Address line 1 is required"),
  line2: Yup.string(),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  postal_code: Yup.string().required("Postal code is required"),
  country: Yup.string().required("Country is required")
})

const BillingAddressForm = ({
  initialValues,
  orgId,
  onSuccess,
  onCancel
}: BillingAddressFormProps) => {
  const handleSubmit = async (values: BillingAddress) => {
    await bbApi.payment.updateBillingAddress({
      org_id: orgId,
      ...values
    })
    onSuccess()
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={addressSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting, dirty }) => (
        <Form className="space-y-4">
          <div>
            <label className="block text-sm text-neutral-400">
              Address Line 1
            </label>
            <Field
              name="line1"
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3"
            />
            {errors.line1 && touched.line1 && (
              <div className="text-sm text-red-600">{errors.line1}</div>
            )}
          </div>

          <div>
            <label className="block text-sm text-neutral-400">
              Address Line 2
            </label>
            <Field
              name="line2"
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400">City</label>
              <Field
                name="city"
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3"
              />
              {errors.city && touched.city && (
                <div className="text-sm text-red-600">{errors.city}</div>
              )}
            </div>

            <div>
              <label className="block text-sm text-neutral-400">State</label>
              <Field
                name="state"
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3"
              />
              {errors.state && touched.state && (
                <div className="text-sm text-red-600">{errors.state}</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-neutral-400">
                Postal Code
              </label>
              <Field
                name="postal_code"
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3"
              />
              {errors.postal_code && touched.postal_code && (
                <div className="text-sm text-red-600">{errors.postal_code}</div>
              )}
            </div>

            <div>
              <label className="block text-sm text-neutral-400">Country</label>
              <Field
                name="country"
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3"
              />
              {errors.country && touched.country && (
                <div className="text-sm text-red-600">{errors.country}</div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <LightBtn onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </LightBtn>
            <PrimaryBtn
              onClick={() => handleSubmit(initialValues)}
              disabled={!dirty}
              isLoading={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Address"}
            </PrimaryBtn>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default BillingAddressForm
